import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { Cookie, X } from 'lucide-react';

const ConsentContainer = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, rgba(71, 69, 69, 0.98) 0%, rgba(0, 151, 178, 0.95) 100%);
  backdrop-filter: blur(20px);
  padding: 24px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
  border-top: 2px solid rgba(0, 151, 178, 0.5);
  z-index: 9999;
`;

const ConsentContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 24px;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    text-align: left;
  }
`;

const TextSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: rgba(250, 236, 236, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const TextContent = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #FAECEC;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: #FAECEC;
  opacity: 0.9;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const LearnMoreLink = styled.a`
  color: #FAECEC;
  text-decoration: underline;
  font-weight: 600;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`;

const AcceptButton = styled(motion.button)`
  background: #FAECEC;
  color: #0097B2;
  border: none;
  padding: 12px 32px;
  border-radius: 50px;
  font-family: 'Comfortaa', sans-serif;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(250, 236, 236, 0.3);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DeclineButton = styled(motion.button)`
  background: transparent;
  color: #FAECEC;
  border: 2px solid rgba(250, 236, 236, 0.5);
  padding: 10px 32px;
  border-radius: 50px;
  font-family: 'Comfortaa', sans-serif;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(250, 236, 236, 0.1);
    border-color: #FAECEC;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  color: #FAECEC;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(250, 236, 236, 0.1);
  }

  @media (min-width: 769px) {
    display: none;
  }
`;

const CookieConsent = () => {
  const [showConsent, setShowConsent] = useState(false);

  useEffect(() => {
    const consentGiven = localStorage.getItem('f01i-cookie-consent');
    if (!consentGiven) {
      // Show consent after a short delay
      setTimeout(() => setShowConsent(true), 1000);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('f01i-cookie-consent', 'accepted');
    setShowConsent(false);
  };

  const handleDecline = () => {
    localStorage.setItem('f01i-cookie-consent', 'declined');
    setShowConsent(false);
  };

  return (
    <AnimatePresence>
      {showConsent && (
        <ConsentContainer
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <CloseButton
            onClick={handleDecline}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <X size={20} />
          </CloseButton>
          
          <ConsentContent>
            <TextSection>
              <IconWrapper>
                <Cookie size={24} color="#FAECEC" />
              </IconWrapper>
              <TextContent>
                <Title>We value your privacy</Title>
                <Description>
                  We use cookies to enhance your browsing experience and analyze our traffic. 
                  By clicking "Accept", you consent to our use of cookies. {' '}
                  <LearnMoreLink href="/cookie-policy">Learn more</LearnMoreLink>
                </Description>
              </TextContent>
            </TextSection>
            
            <ButtonGroup>
              <AcceptButton
                onClick={handleAccept}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Accept
              </AcceptButton>
              <DeclineButton
                onClick={handleDecline}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Decline
              </DeclineButton>
            </ButtonGroup>
          </ConsentContent>
        </ConsentContainer>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
